import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import {
  AccountReportFilterActions,
  AccountReportPageHeader,
  AccountReportTableShell,
} from "@/components/accounts/AccountReportShell";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { EmployeeSelect, employeePickerLabel } from "@/components/hr/EmployeeSelect";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { renderBalance } from "@/lib/accounts/ledgerReportDisplay";
import { formatReportDate, money } from "@/lib/accounts/ledgerReportUtils";
import {
  downloadEmployeeLedgerExcel,
  downloadEmployeeLedgerPdf,
  fetchEmployeeLedger,
  type EmployeeLedger,
} from "@/lib/hr/employeeLedgerStore";
import { listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { BookOpen } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

const EmployeeLedgerPage = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "employee_can_view_any");

  const [isForbidden, setIsForbidden] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [hasApplied, setHasApplied] = useState(false);
  const [ledger, setLedger] = useState<EmployeeLedger | null>(null);
  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [employeeId, setEmployeeId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const currencySymbol = user?.company_branding?.currency?.symbol?.trim() || "";

  const activeFilters = useMemo(
    () => ({
      employeeId,
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
    }),
    [employeeId, dateFrom, dateTo],
  );

  useEffect(() => {
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("auth_user_updated", onAuth);
    return () => window.removeEventListener("auth_user_updated", onAuth);
  }, []);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    void (async () => {
      try {
        await syncEmployeesFromApi();
        if (!cancelled) setEmployees(listEmployees());
      } catch (error) {
        console.error("[EmployeeLedger] Employees load failed", error);
        if (!cancelled) setEmployees(listEmployees());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canView]);

  const applyFilters = useCallback(async () => {
    if (!canView) return;
    if (!employeeId.trim()) {
      toast.error("Please select an employee.");
      return;
    }
    setLoading(true);
    setIsForbidden(false);
    try {
      const data = await fetchEmployeeLedger(activeFilters);
      setLedger(data);
      setHasApplied(true);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        setIsForbidden(true);
        return;
      }
      if (isAxiosError(error) && error.response?.status === 422) {
        toast.error(String(error.response?.data?.message ?? "Please select a valid employee."));
        return;
      }
      console.error("[EmployeeLedger] Load failed", error);
      toast.error("Could not load employee ledger.");
    } finally {
      setLoading(false);
    }
  }, [canView, activeFilters]);

  const exportReport = useCallback(
    async (format: "pdf" | "excel") => {
      if (!hasApplied || !employeeId.trim() || exporting) return;
      setExporting(format);
      try {
        if (format === "pdf") {
          await downloadEmployeeLedgerPdf(activeFilters);
          toast.success("PDF downloaded.");
        } else {
          await downloadEmployeeLedgerExcel(activeFilters);
          toast.success("Excel downloaded.");
        }
      } catch (error) {
        console.error(`[EmployeeLedger] ${format} export failed`, error);
        toast.error(format === "pdf" ? "Could not download PDF." : "Could not download Excel.");
      } finally {
        setExporting(null);
      }
    },
    [hasApplied, employeeId, exporting, activeFilters],
  );

  const resetFilters = () => {
    setEmployeeId("");
    setDateFrom("");
    setDateTo("");
    setLedger(null);
    setHasApplied(false);
  };

  const selectedEmployee = employees.find((e) => String(e.id) === String(employeeId));
  const employeeLabel =
    ledger?.employee != null
      ? ledger.employee.code
        ? `${ledger.employee.code} · ${ledger.employee.name}`
        : ledger.employee.name
      : selectedEmployee
        ? employeePickerLabel(selectedEmployee)
        : null;

  if (!canView || isForbidden) {
    return (
      <Layout>
        <PermissionDenied />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5">
        <AccountReportPageHeader
          icon={<BookOpen className="h-5 w-5 text-primary" />}
          title="Employee Ledger"
          description="Accounting sub-ledger for an employee from posted journal entries (loans, salary advances, and salaries payable)."
        />

        <div className="rounded-2xl border border-border/55 bg-card/80 p-4 shadow-sm ring-1 ring-border/25">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs font-semibold text-foreground">Employee</Label>
              <EmployeeSelect
                value={employeeId}
                onChange={setEmployeeId}
                employees={employees}
                placeholder="Select employee"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">From date</Label>
              <DatePartsPicker
                value={dateFrom}
                onChange={setDateFrom}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                maxDate={dateTo ? new Date(`${dateTo}T00:00:00`) : undefined}
                placeholder="Select from date"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">To date</Label>
              <DatePartsPicker
                value={dateTo}
                onChange={setDateTo}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                minDate={dateFrom ? new Date(`${dateFrom}T00:00:00`) : undefined}
                placeholder="Select to date"
                align="end"
              />
            </div>
          </div>

          <AccountReportFilterActions
            hasApplied={hasApplied}
            loading={loading}
            exporting={exporting}
            onApply={() => void applyFilters()}
            onReset={resetFilters}
            onExport={(format) => void exportReport(format)}
            statusText={
              hasApplied ? (
                <>
                  {employeeLabel ? (
                    <>
                      Showing ledger for <span className="font-semibold text-foreground">{employeeLabel}</span>
                    </>
                  ) : (
                    "Showing employee ledger"
                  )}
                  {dateFrom ? (
                    <>
                      {" · From "}
                      <span className="font-semibold text-foreground">{formatReportDate(dateFrom)}</span>
                    </>
                  ) : null}
                  {dateTo ? (
                    <>
                      {" · To "}
                      <span className="font-semibold text-foreground">{formatReportDate(dateTo)}</span>
                    </>
                  ) : null}
                </>
              ) : (
                "Select an employee and optional dates, then click Apply filter."
              )
            }
          />
        </div>

        <AccountReportTableShell
          loading={loading}
          hasApplied={hasApplied}
          notAppliedMessage="No ledger loaded yet. Select an employee and apply a filter."
        >
          {!ledger ? (
            <p className="py-12 text-center text-sm text-muted-foreground">No ledger data.</p>
          ) : (
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date / Voucher #</TableHead>
                    <TableHead>Particulars</TableHead>
                    <TableHead>From / To</TableHead>
                    <TableHead className="text-right">Debit{currencySymbol ? ` (${currencySymbol})` : ""}</TableHead>
                    <TableHead className="text-right">Credit{currencySymbol ? ` (${currencySymbol})` : ""}</TableHead>
                    <TableHead className="text-right">Balance{currencySymbol ? ` (${currencySymbol})` : ""}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="bg-muted/30">
                    <TableCell className="font-semibold text-foreground" colSpan={3}>
                      Opening Balance
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground" />
                    <TableCell className="text-right tabular-nums text-muted-foreground" />
                    <TableCell className="text-right tabular-nums font-semibold">
                      {renderBalance(ledger.totals.opening_balance, ledger.totals.opening_balance_side)}
                    </TableCell>
                  </TableRow>
                  {ledger.lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                        No posted entries in this period.
                      </TableCell>
                    </TableRow>
                  ) : (
                    ledger.lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell className="min-w-[8rem] whitespace-nowrap">
                          <div className="text-sm text-foreground">{formatReportDate(line.date)}</div>
                          <div className="font-mono text-[11px] text-muted-foreground">{line.voucher || ""}</div>
                          {line.voucher_type ? (
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                              {line.voucher_type}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell className="min-w-[10rem] max-w-[18rem]">
                          <div className="space-y-0.5">
                            {line.particulars.length > 0 ? (
                              line.particulars.map((text, idx) => (
                                <p
                                  key={`${line.id}-p-${idx}`}
                                  className={cn(
                                    "leading-snug",
                                    idx === 0
                                      ? "text-sm font-medium text-foreground"
                                      : "text-[11px] text-muted-foreground",
                                  )}
                                >
                                  {text}
                                </p>
                              ))
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="min-w-[8rem] max-w-[14rem] text-xs text-muted-foreground leading-snug">
                          {line.from_to || ""}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                          {money(line.debit, true)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-rose-700 dark:text-rose-400">
                          {money(line.credit, true)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium">
                          {renderBalance(line.balance, line.balance_side)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={3} className="font-semibold text-foreground">
                      Closing Balance
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{money(ledger.totals.debit)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{money(ledger.totals.credit)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      {renderBalance(ledger.totals.balance, ledger.totals.balance_side)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          )}
        </AccountReportTableShell>
      </div>
    </Layout>
  );
};

export default EmployeeLedgerPage;
